// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { OccViewer, } from "../../../core/viewer/viewer.js";
import { MedicalUtils } from "../../../modules/medical/utils/utils.js";
import { OperationSide, SelectionOption, CTMode, } from "./patient-info.js";
import { Mat4Tools } from "../../../core/geometry/mat4-tools.js";
import { PlaneTools } from "../../../core/geometry/plane-tools.js";
import { Vec3Tools } from "../../../core/geometry/vec3-tools.js";
import { StemHeadUtilities, StemPlacementType } from "./stem-head-utilities.js";
import { LinerCupUtilities } from "./liner-cup-utilities.js";
import { PelvisTiltUtils } from "./pelvis-tilt-utils.js";
/**
 * Tool for patient calculations.
 */
export class PatientUtils {
    /** Return identity matrix. */
    constructor(theCtx) { this._myCtx = theCtx; }
    /** Get context*/
    get context() { return this._myCtx; }
    /** Get current shell transformation */
    get shellTransform() {
        const shellTransform = this.context.current.transform.shellTGlobal;
        return shellTransform;
    }
    /** Get current liner transformation */
    get linerTransform() {
        const linerTransform = this.context.current.transform.linerTGlobal;
        return linerTransform;
    }
    /** Get current insert transformation */
    get insertTransform() {
        const insertTransform = this.context.current.transform.insertTGlobal;
        return insertTransform;
    }
    /** Get current head transformation */
    get headTransform() { return this.context.current.transform.headTGlobal; }
    /** Get shell center */
    get shellCenter() {
        let aShellTrans = Mat4Tools.unscale(this.shellTransform);
        return Vec3Tools.applyMatrix(aShellTrans, this.context.engineering.shellInfo.rotationPoint);
    }
    /** Get liner center */
    get linerCenter() {
        let aLinerTrans = Mat4Tools.unscale(this.linerTransform);
        return Vec3Tools.applyMatrix(aLinerTrans, this.context.engineering.linerInfo.rotationPoint);
    }
    /** Get insert center */
    get insertCenter() {
        let anInsertTrans = Mat4Tools.unscale(this.insertTransform);
        return Vec3Tools.applyMatrix(anInsertTrans, this.context.engineering.insertInfo.rotationPoint);
    }
    /** Get head center */
    get headCenter() { return Mat4Tools.getTranslation(this.headTransform); }
    /** Get adjust shell transform */
    get adjustShellTransform() {
        const shellTransform = this.context.current.customTransform.adjustViewShell;
        return shellTransform;
    }
    /** Get adjust liner transform */
    get adjustLinerTransform() {
        const linerTransform = this.context.current.customTransform.adjustViewLiner;
        return linerTransform;
    }
    /** Get adjust head transform */
    get adjustHeadTransform() {
        return this.context.current.customTransform.adjustViewHead;
    }
    /** Get adjust shell center */
    get adjustShellCenter() {
        let aShellTrans = Mat4Tools.unscale(this.adjustShellTransform);
        return Vec3Tools.applyMatrix(aShellTrans, this.context.engineering.shellInfo.rotationPoint);
    }
    /** Get adjust liner center */
    get adjustLinerCenter() {
        let aLinerTrans = Mat4Tools.unscale(this.adjustLinerTransform);
        return Vec3Tools.applyMatrix(aLinerTrans, this.context.engineering.linerInfo.rotationPoint);
    }
    /** Get adjust head center */
    get adjustHeadCenter() {
        return Mat4Tools.getTranslation(this.adjustHeadTransform);
    }
    /** Get resection planes */
    get resectionPlanes() {
        return StemHeadUtilities.computeStemResectionPlanes(this.context.engineering.stemInfo, this.context.current.transform.stemTGlobal);
    }
    /** Get adjust view resection planes */
    get adjustViewResectionPlanes() {
        return StemHeadUtilities.computeStemResectionPlanes(this.context.engineering.stemInfo, this.context.current.customTransform.adjustViewStem);
    }
    /** Get 2D presentation for resection planes */
    get resectionPlanes2D() {
        const aResPlanes = StemHeadUtilities.computeStemResectionPlanes(this.context.engineering.stemInfo, this.context.current.customTransform.adjustViewStem);
        const aLin = PlaneTools.intersect(aResPlanes[0], aResPlanes[1]);
        let aLin1 = PlaneTools.intersect(aResPlanes[0], PlaneTools.zx());
        let aLin2 = PlaneTools.intersect(aResPlanes[1], PlaneTools.zx());
        if (Vec3Tools.dot(aLin1.direction, aResPlanes[1].normal) > 0.0) {
            aLin1.direction = Vec3Tools.reverse(aLin1.direction);
        }
        if (Vec3Tools.dot(aLin2.direction, aResPlanes[0].normal) > 0.0) {
            aLin2.direction = Vec3Tools.reverse(aLin2.direction);
        }
        aLin1.location = aLin.location;
        aLin2.location = aLin.location;
        return [aLin1, aLin2];
    }
    /**
     * Get anterior pelvic plane
    */
    get APP() {
        const aCtx = this.context;
        const rAsis = aCtx.current.landmarks.pelvisRightAsisLandmark;
        const lAsis = aCtx.current.landmarks.pelvisLeftAsisLandmark;
        const rSymph = aCtx.current.landmarks.pelvisRightPubicSymphysisLandmark;
        const lSymph = aCtx.current.landmarks.pelvisLeftPubicSymphysisLandmark;
        return PelvisTiltUtils.getAPP(rAsis, lAsis, rSymph, lSymph);
    }
    /**
     * Get Pelvis Tilt
     * @returns angle in radians
     */
    get pelvisTilt() {
        const aCtx = this.context;
        const rAsis = aCtx.current.landmarks.pelvisRightAsisLandmark;
        const lAsis = aCtx.current.landmarks.pelvisLeftAsisLandmark;
        const rSymph = aCtx.current.landmarks.pelvisRightPubicSymphysisLandmark;
        const lSymph = aCtx.current.landmarks.pelvisLeftPubicSymphysisLandmark;
        return PelvisTiltUtils.getPelvisTilt(rAsis, lAsis, rSymph, lSymph);
    }
    /**
     * Set Pelvis Tilt
     * @param {number} theAngle - angle in radians
    */
    set pelvisTilt(theAngle) {
        //! Get current pelvisTilt
        let aCurPT = this.pelvisTilt;
        const aCtx = this.context;
        //! rotation axis
        const rAsis = aCtx.current.landmarks.pelvisRightAsisLandmark;
        const lAsis = aCtx.current.landmarks.pelvisLeftAsisLandmark;
        const aRelativeTransRotation = PelvisTiltUtils.getRelativePelvisTiltTransform(rAsis, lAsis, aCurPT, theAngle);
        const anOldNonOpFemurCorPos = aCtx.engineering.operationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurLeftCorLandmark
            : aCtx.current.landmarks.femurRightCorLandmark;
        const aFemurPostopToPreopTrsf = aCtx.engineering.operationSide == OperationSide.Right
            ? Mat4Tools.multiply(aCtx.current.transform.femurRightPreopTGlobal, Mat4Tools.invert(aCtx.current.transform.femurRightTGlobal))
            : Mat4Tools.multiply(aCtx.current.transform.femurLeftPreopTGlobal, Mat4Tools.invert(aCtx.current.transform.femurLeftTGlobal));
        const anOldPreOpFemurCorPos = aCtx.engineering.operationSide == OperationSide.Right
            ? Vec3Tools.applyMatrix(aFemurPostopToPreopTrsf, aCtx.current.landmarks.femurRightCorLandmark)
            : Vec3Tools.applyMatrix(aFemurPostopToPreopTrsf, aCtx.current.landmarks.femurLeftCorLandmark);
        const aTransPt = (aPoint) => {
            return Vec3Tools.applyMatrix(aRelativeTransRotation, aPoint);
        };
        const aTransMat = (aMat) => {
            return Mat4Tools.multiply(aRelativeTransRotation, aMat);
        };
        const aNewNonOpFemurCorPos = aTransPt(anOldNonOpFemurCorPos);
        const aNonOpFemurCorDelta = Vec3Tools.subtract(aNewNonOpFemurCorPos, anOldNonOpFemurCorPos);
        const aNonOpFemurCorDeltaMat = Mat4Tools.translation(aNonOpFemurCorDelta);
        const aNonOpMove = (aPoint) => {
            return Vec3Tools.add(aPoint, aNonOpFemurCorDelta);
        };
        const aNonOpMatMove = (aMat) => {
            return Mat4Tools.multiply(aNonOpFemurCorDeltaMat, aMat);
        };
        const aNewPreOpFemurCorPos = aTransPt(anOldPreOpFemurCorPos);
        const aPreOpFemurCorDelta = Vec3Tools.subtract(aNewPreOpFemurCorPos, anOldPreOpFemurCorPos);
        const aPreOpFemurCorDeltaMat = Mat4Tools.translation(aPreOpFemurCorDelta);
        const aPreopOpMatMove = (aMat) => {
            return Mat4Tools.multiply(aPreOpFemurCorDeltaMat, aMat);
        };
        const landmarksToRotate = [
            'pelvisLeftAsisLandmark',
            'pelvisRightAsisLandmark',
            'pelvisLeftPubicSymphysisLandmark',
            'pelvisRightPubicSymphysisLandmark',
            'pelvisLeftCorLandmark',
            'pelvisRightCorLandmark',
        ];
        const transformsToRotate = [
            'pelvisTGlobal',
            'shellTGlobal',
            'linerTGlobal',
        ];
        const landmarksToMoveLeft = [
            'femurLeftCorLandmark',
            'femurLeftLtLandmark',
            'femurLeftLtProxLandmark',
            'femurLeftGtLandmark',
            'femurLeftCondyleMedLandmark',
            'femurLeftCondyleLatLandmark',
            'femurLeftAxisStartLandmark',
            'femurLeftAxisEndLandmark',
        ];
        const transformsToMoveLeft = [
            'femurLeftTGlobal',
        ];
        const transformsToMovePreopLeft = [
            'femurLeftPreopTGlobal',
        ];
        const landmarksToMoveRight = [
            'femurRightCorLandmark',
            'femurRightLtLandmark',
            'femurRightLtProxLandmark',
            'femurRightGtLandmark',
            'femurRightCondyleMedLandmark',
            'femurRightCondyleLatLandmark',
            'femurRightAxisStartLandmark',
            'femurRightAxisEndLandmark',
        ];
        const transformsToMoveRight = [
            'femurRightTGlobal',
        ];
        const transformsToMovePreopRight = [
            'femurRightPreopTGlobal',
        ];
        const transformsToMove = [
            'headTGlobal',
            'insertTGlobal',
            'stemTGlobal',
        ];
        const customTransformsToMove = [
            'adjustViewHead',
            'adjustViewInsert',
            'adjustViewStem',
            'adjustViewFemur',
        ];
        const customTransformsToRotate = [
            'adjustViewShell',
            'adjustViewLiner',
        ];
        // Get old liner COR (Before applying pelvic tilt)
        const anOldLinerCenter = this.linerCenter;
        const anOldEngLinerCor = Vec3Tools.applyMatrix(Mat4Tools.unscale(aCtx.engineering.transform.linerTGlobal), aCtx.engineering.linerInfo.rotationPoint);
        landmarksToRotate.forEach((aLandmark) => {
            aCtx.current.landmarks[aLandmark] = aTransPt(aCtx.current.landmarks[aLandmark]);
            aCtx.engineering.landmarks[aLandmark] = aTransPt(aCtx.engineering.landmarks[aLandmark]);
        });
        transformsToRotate.forEach((aTransform) => {
            aCtx.current.transform[aTransform] = aTransMat(aCtx.current.transform[aTransform]);
            aCtx.engineering.transform[aTransform] = aTransMat(aCtx.engineering.transform[aTransform]);
        });
        // Get new liner COR (After applying pelvic tilt)
        const aNewLinerCenter = this.linerCenter;
        const aNewEngLinerCor = Vec3Tools.applyMatrix(Mat4Tools.unscale(aCtx.engineering.transform.linerTGlobal), aCtx.engineering.linerInfo.rotationPoint);
        // Calculate the liner COR offset to apply to operative implants/bones
        const aDeltaTrans = Vec3Tools.subtract(aNewLinerCenter, anOldLinerCenter);
        const aDeltaTransMat = Mat4Tools.translation(aDeltaTrans);
        const aDeltaTransEng = Vec3Tools.subtract(aNewEngLinerCor, anOldEngLinerCor);
        const aDeltaTransEngMat = Mat4Tools.translation(aDeltaTransEng);
        const aMove = (aPoint) => {
            return Vec3Tools.add(aPoint, aDeltaTrans);
        };
        const aMoveEng = (aPoint) => {
            return Vec3Tools.add(aPoint, aDeltaTransEng);
        };
        const aMatMove = (aMat) => {
            return Mat4Tools.multiply(aDeltaTransMat, aMat);
        };
        const aMatMoveEng = (aMat) => {
            return Mat4Tools.multiply(aDeltaTransEngMat, aMat);
        };
        const transformsToMoveOpSide = [...transformsToMove];
        const transformsToMovePreopSide = [];
        const transformsToMoveNonOpSide = [];
        const landmarksToMoveOpSide = [];
        const landmarksToMoveNonOpSide = [];
        if (aCtx.engineering.operationSide == OperationSide.Right) {
            transformsToMoveOpSide.push(...transformsToMoveRight);
            transformsToMovePreopSide.push(...transformsToMovePreopRight);
            transformsToMoveNonOpSide.push(...transformsToMoveLeft);
            transformsToMoveNonOpSide.push(...transformsToMovePreopLeft);
            landmarksToMoveOpSide.push(...landmarksToMoveRight);
            landmarksToMoveNonOpSide.push(...landmarksToMoveLeft);
        }
        else {
            transformsToMoveOpSide.push(...transformsToMoveLeft);
            transformsToMovePreopSide.push(...transformsToMovePreopLeft);
            transformsToMoveNonOpSide.push(...transformsToMoveRight);
            transformsToMoveNonOpSide.push(...transformsToMovePreopRight);
            landmarksToMoveOpSide.push(...landmarksToMoveLeft);
            landmarksToMoveNonOpSide.push(...landmarksToMoveRight);
        }
        transformsToMoveOpSide.forEach((aTransform) => {
            aCtx.current.transform[aTransform] = aMatMove(aCtx.current.transform[aTransform]);
            aCtx.engineering.transform[aTransform] = aMatMoveEng(aCtx.engineering.transform[aTransform]);
        });
        transformsToMovePreopSide.forEach((aTransform) => {
            aCtx.current.transform[aTransform] = aPreopOpMatMove(aCtx.current.transform[aTransform]);
            aCtx.engineering.transform[aTransform] = aPreopOpMatMove(aCtx.engineering.transform[aTransform]);
        });
        transformsToMoveNonOpSide.forEach((aTransform) => {
            aCtx.current.transform[aTransform] = aNonOpMatMove(aCtx.current.transform[aTransform]);
            aCtx.engineering.transform[aTransform] = aNonOpMatMove(aCtx.engineering.transform[aTransform]);
        });
        landmarksToMoveOpSide.forEach((aLandmark) => {
            aCtx.current.landmarks[aLandmark] = aMove(aCtx.current.landmarks[aLandmark]);
            aCtx.engineering.landmarks[aLandmark] = aMoveEng(aCtx.engineering.landmarks[aLandmark]);
        });
        landmarksToMoveNonOpSide.forEach((aLandmark) => {
            aCtx.current.landmarks[aLandmark] = aNonOpMove(aCtx.current.landmarks[aLandmark]);
            aCtx.engineering.landmarks[aLandmark] = aNonOpMove(aCtx.engineering.landmarks[aLandmark]);
        });
        customTransformsToMove.forEach((aTransform) => {
            aCtx.current.customTransform[aTransform] = aMatMove(aCtx.current.customTransform[aTransform]);
        });
        customTransformsToRotate.forEach((aTransform) => {
            aCtx.current.customTransform[aTransform] = aTransMat(aCtx.current.customTransform[aTransform]);
        });
    }
    /**
     * Get COR shift
     * Computed value
     * @returns {CorShift}  COR shift
     */
    get corShift() {
        const aCtx = this.context;
        const operationSide = aCtx.engineering.operationSide;
        //! pre op COR center (acetabulum cor)
        const preOpCorCenter = operationSide == OperationSide.Right
            ? aCtx.engineering.landmarks.pelvisRightCorLandmark
            : aCtx.engineering.landmarks.pelvisLeftCorLandmark;
        //! pre op COR center on opposite side (acetabulum cor)
        const oppositePreOpCorCenter = operationSide == OperationSide.Right
            ? aCtx.engineering.landmarks.pelvisLeftCorLandmark
            : aCtx.engineering.landmarks.pelvisRightCorLandmark;
        //! pre op COR center on opposite side (acetabulum cor) mirrored by sagittal plane
        const oppositePreOpCorCenterMirrored = {
            x: -oppositePreOpCorCenter.x,
            y: oppositePreOpCorCenter.y,
            z: oppositePreOpCorCenter.z,
        };
        //! liner center
        const linerCorCenter = this.linerCenter;
        //! Medial multiplier. Negate medial value if case side is left.
        const medialMultiplier = operationSide == OperationSide.Right ? 1.0 : -1.0;
        //! pre op axes
        let preOpAxes = {
            medial: (linerCorCenter.x - preOpCorCenter.x) * medialMultiplier,
            posterior: (linerCorCenter.y - preOpCorCenter.y),
            superior: linerCorCenter.z - preOpCorCenter.z,
        };
        //! opposite axes
        let opposite = {
            medial: (linerCorCenter.x - oppositePreOpCorCenterMirrored.x) * medialMultiplier,
            posterior: (linerCorCenter.y - oppositePreOpCorCenterMirrored.y),
            superior: linerCorCenter.z - oppositePreOpCorCenterMirrored.z,
        };
        //! COR shift
        const corShift = { preOp: preOpAxes, opposite: opposite };
        return corShift;
    }
    /**
     * Adjusts plan move cup.
     * Will update cup/stem/bone transformations.
     * Will call adjustPlanMoveStem.
     */
    adjustPlanMoveCup() {
        const aCtx = this.context;
        aCtx.current.transform.linerTGlobal = aCtx.current.customTransform.adjustViewLiner;
        aCtx.current.transform.shellTGlobal = aCtx.current.customTransform.adjustViewShell;
        this.adjustPlanMoveStem();
    }
    /**
     * Set new cup
     * @param theShellInfo shell info
     * @param theLinerInfo liner info
     */
    setCup(theShellInfo, theLinerInfo) {
        if (theLinerInfo.scale <= 0.0 || theShellInfo.scale <= 0.0) {
            console.error("Error: setCup - implant data are not valid");
            return;
        }
        const aCtx = this.context;
        const isValidOldLiner = LinerCupUtilities.isValidLiner(aCtx.engineering.linerInfo);
        const isValidOldShell = LinerCupUtilities.isValidShell(aCtx.engineering.shellInfo);
        const rescale = (theTrsf, theNewScale, theOldScale) => {
            const aTrsf = Mat4Tools.multiply(theTrsf, Mat4Tools.scale(theNewScale / theOldScale));
            return aTrsf;
        };
        let isCTFreeInitialization = false;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree && !isValidOldLiner
            && !isValidOldShell) {
            isCTFreeInitialization = true;
            const anOperationSide = aCtx.engineering.operationSide;
            const aTranslation = anOperationSide == OperationSide.Right
                ? Mat4Tools.translation(aCtx.engineering.landmarks.pelvisRightCorLandmark)
                : Mat4Tools.translation(aCtx.engineering.landmarks.pelvisLeftCorLandmark);
            const aXDirSign = anOperationSide == OperationSide.Right ? 1 : -1;
            const aCS = {
                location: { x: 0, y: 0, z: 0 },
                xDirection: { x: 0, y: aXDirSign, z: 0 },
                direction: { x: 0, y: 0, z: -1 }
            };
            const aRotation = Mat4Tools.getCSTransformation(aCS);
            const aLinerScale = Mat4Tools.scale(theLinerInfo.scale);
            const aShellScale = Mat4Tools.scale(theShellInfo.scale);
            const aLinerTransformation = Mat4Tools.multiply(aTranslation, Mat4Tools.multiply(aRotation, aLinerScale));
            const aShellTransformation = Mat4Tools.multiply(aTranslation, Mat4Tools.multiply(aRotation, aShellScale));
            aCtx.current.transform.linerTGlobal = aLinerTransformation;
            aCtx.engineering.transform.linerTGlobal = aLinerTransformation;
            aCtx.current.customTransform.adjustViewLiner = aLinerTransformation;
            aCtx.current.transform.shellTGlobal = aShellTransformation;
            aCtx.engineering.transform.shellTGlobal = aShellTransformation;
            aCtx.current.customTransform.adjustViewShell = aShellTransformation;
        }
        else {
            //! rescale liner
            const oldLinerScale = isValidOldLiner ? aCtx.engineering.linerInfo.scale : 1;
            const newLinerScale = theLinerInfo.scale;
            if (oldLinerScale != newLinerScale) {
                aCtx.current.transform.linerTGlobal = rescale(aCtx.current.transform.linerTGlobal, newLinerScale, oldLinerScale);
                aCtx.engineering.transform.linerTGlobal = rescale(aCtx.engineering.transform.linerTGlobal, newLinerScale, oldLinerScale);
                aCtx.current.customTransform.adjustViewLiner =
                    rescale(aCtx.current.customTransform.adjustViewLiner, newLinerScale, oldLinerScale);
            }
            //! rescale shell
            const oldShellScale = isValidOldShell ? aCtx.engineering.shellInfo.scale : 1;
            const newShellScale = theShellInfo.scale;
            if (oldShellScale != newShellScale) {
                aCtx.current.transform.shellTGlobal = rescale(aCtx.current.transform.shellTGlobal, newShellScale, oldShellScale);
                aCtx.engineering.transform.shellTGlobal = rescale(aCtx.engineering.transform.shellTGlobal, newShellScale, oldShellScale);
                aCtx.current.customTransform.adjustViewShell =
                    rescale(aCtx.current.customTransform.adjustViewShell, newShellScale, oldShellScale);
            }
        }
        if (isValidOldShell && aCtx.engineering.shellInfo.liEpOffset != theShellInfo.liEpOffset) {
            const aCurCtx = this.context.current;
            const anEngCtx = this.context.engineering;
            let aRelativeTrans = (theShellTrsf) => {
                const aShellRotation = Mat4Tools.getRotation(theShellTrsf);
                const anOffsetVec = Vec3Tools.applyMatrix(aShellRotation, { x: 0, y: 0, z: -1 });
                const anOldShellPnt = Vec3Tools.multiply(anOffsetVec, anEngCtx.shellInfo.liEpOffset);
                const anNewShellPnt = Vec3Tools.multiply(anOffsetVec, theShellInfo.liEpOffset);
                return Mat4Tools.translation(Vec3Tools.subtract(anNewShellPnt, anOldShellPnt));
            };
            let aTransform = (theOldTrsf, theShellTrsf) => {
                return Mat4Tools.multiply(aRelativeTrans(theShellTrsf), theOldTrsf);
            };
            // Adjust Cup with Cup LiEp offset
            anEngCtx.transform.shellTGlobal = aTransform(anEngCtx.transform.shellTGlobal, anEngCtx.transform.shellTGlobal);
            aCurCtx.transform.shellTGlobal = aTransform(aCurCtx.transform.shellTGlobal, aCurCtx.transform.shellTGlobal);
            aCurCtx.customTransform.adjustViewShell =
                aTransform(aCurCtx.customTransform.adjustViewShell, aCurCtx.customTransform.adjustViewShell);
            // Adjust Liner with Cup LiEp offset
            anEngCtx.transform.linerTGlobal = aTransform(anEngCtx.transform.linerTGlobal, anEngCtx.transform.shellTGlobal);
            aCurCtx.transform.linerTGlobal = aTransform(aCurCtx.transform.linerTGlobal, aCurCtx.transform.shellTGlobal);
            aCurCtx.customTransform.adjustViewLiner =
                aTransform(aCurCtx.customTransform.adjustViewLiner, aCurCtx.customTransform.adjustViewShell);
            // Adjust Stem with Cup LiEp offset
            anEngCtx.transform.stemTGlobal = aTransform(anEngCtx.transform.stemTGlobal, anEngCtx.transform.shellTGlobal);
            aCurCtx.transform.stemTGlobal = aTransform(aCurCtx.transform.stemTGlobal, aCurCtx.transform.shellTGlobal);
            aCurCtx.customTransform.adjustViewStem = aTransform(aCurCtx.customTransform.adjustViewStem, aCurCtx.customTransform.adjustViewShell);
            // Adjust Head with Cup LiEp offset
            anEngCtx.transform.headTGlobal = aTransform(anEngCtx.transform.headTGlobal, anEngCtx.transform.shellTGlobal);
            aCurCtx.transform.headTGlobal = aTransform(aCurCtx.transform.headTGlobal, aCurCtx.transform.shellTGlobal);
            aCurCtx.customTransform.adjustViewHead = aTransform(aCurCtx.customTransform.adjustViewHead, aCurCtx.customTransform.adjustViewShell);
            // Adjust Insert with Cup LiEp offset
            anEngCtx.transform.insertTGlobal = aTransform(anEngCtx.transform.insertTGlobal, anEngCtx.transform.shellTGlobal);
            aCurCtx.transform.insertTGlobal = aTransform(aCurCtx.transform.insertTGlobal, aCurCtx.transform.shellTGlobal);
            aCurCtx.customTransform.adjustViewInsert =
                aTransform(aCurCtx.customTransform.adjustViewInsert, aCurCtx.customTransform.adjustViewShell);
            // Adjust Femur with Cup LiEp offset
            aCurCtx.customTransform.adjustViewFemur =
                aTransform(aCurCtx.customTransform.adjustViewFemur, aCurCtx.customTransform.adjustViewShell);
            if (anEngCtx.operationSide == OperationSide.Right) {
                anEngCtx.transform.femurRightTGlobal = aTransform(anEngCtx.transform.femurRightTGlobal, anEngCtx.transform.shellTGlobal);
                aCurCtx.transform.femurRightTGlobal = aTransform(aCurCtx.transform.femurRightTGlobal, aCurCtx.transform.shellTGlobal);
            }
            else {
                anEngCtx.transform.femurLeftTGlobal = aTransform(anEngCtx.transform.femurLeftTGlobal, anEngCtx.transform.shellTGlobal);
                aCurCtx.transform.femurLeftTGlobal = aTransform(aCurCtx.transform.femurLeftTGlobal, aCurCtx.transform.shellTGlobal);
            }
            this.updateFemurEngineeringLandmarks(aRelativeTrans(anEngCtx.transform.shellTGlobal));
        }
        const oldlinerRotationPoint = isCTFreeInitialization ? { x: 0, y: 0, z: 0 }
            : aCtx.engineering.linerInfo.rotationPoint;
        if ((isCTFreeInitialization || isValidOldLiner)
            && !Vec3Tools.isEqual(oldlinerRotationPoint, theLinerInfo.rotationPoint)) {
            const aCurCtx = this.context.current;
            const anEngCtx = this.context.engineering;
            let aRelativeTrans = (theLinerTrsf) => {
                const anUnscaledLinerTrsf = Mat4Tools.unscale(theLinerTrsf);
                const anOldRotationPnt = Vec3Tools.applyMatrix(anUnscaledLinerTrsf, oldlinerRotationPoint);
                const anNewRotationPnt = Vec3Tools.applyMatrix(anUnscaledLinerTrsf, theLinerInfo.rotationPoint);
                return Mat4Tools.translation(Vec3Tools.subtract(anNewRotationPnt, anOldRotationPnt));
            };
            let aTransform = (theOldTrsf, theLinerTrsf) => {
                return Mat4Tools.multiply(aRelativeTrans(theLinerTrsf), theOldTrsf);
            };
            // Adjust Stem with Liner COR offset
            anEngCtx.transform.stemTGlobal = aTransform(anEngCtx.transform.stemTGlobal, anEngCtx.transform.linerTGlobal);
            aCurCtx.transform.stemTGlobal = aTransform(aCurCtx.transform.stemTGlobal, aCurCtx.transform.linerTGlobal);
            aCurCtx.customTransform.adjustViewStem = aTransform(aCurCtx.customTransform.adjustViewStem, aCurCtx.customTransform.adjustViewLiner);
            // Adjust Head with Liner COR offset
            anEngCtx.transform.headTGlobal = aTransform(anEngCtx.transform.headTGlobal, anEngCtx.transform.linerTGlobal);
            aCurCtx.transform.headTGlobal = aTransform(aCurCtx.transform.headTGlobal, aCurCtx.transform.linerTGlobal);
            aCurCtx.customTransform.adjustViewHead = aTransform(aCurCtx.customTransform.adjustViewHead, aCurCtx.customTransform.adjustViewLiner);
            // Adjust Insert with Liner COR offset
            anEngCtx.transform.insertTGlobal = aTransform(anEngCtx.transform.insertTGlobal, anEngCtx.transform.linerTGlobal);
            aCurCtx.transform.insertTGlobal = aTransform(aCurCtx.transform.insertTGlobal, aCurCtx.transform.linerTGlobal);
            aCurCtx.customTransform.adjustViewInsert =
                aTransform(aCurCtx.customTransform.adjustViewInsert, aCurCtx.customTransform.adjustViewLiner);
            // Adjust Femur with Liner COR offset
            aCurCtx.customTransform.adjustViewFemur =
                aTransform(aCurCtx.customTransform.adjustViewFemur, aCurCtx.customTransform.adjustViewLiner);
            if (anEngCtx.operationSide == OperationSide.Right) {
                anEngCtx.transform.femurRightTGlobal = aTransform(anEngCtx.transform.femurRightTGlobal, anEngCtx.transform.linerTGlobal);
                aCurCtx.transform.femurRightTGlobal = aTransform(aCurCtx.transform.femurRightTGlobal, aCurCtx.transform.linerTGlobal);
            }
            else {
                anEngCtx.transform.femurLeftTGlobal = aTransform(anEngCtx.transform.femurLeftTGlobal, anEngCtx.transform.linerTGlobal);
                aCurCtx.transform.femurLeftTGlobal = aTransform(aCurCtx.transform.femurLeftTGlobal, aCurCtx.transform.linerTGlobal);
            }
            this.updateFemurEngineeringLandmarks(aRelativeTrans(anEngCtx.transform.linerTGlobal));
        }
        aCtx.engineering.linerInfo = theLinerInfo;
        aCtx.engineering.shellInfo = theShellInfo;
        this.updateLandmarks();
    }
    /**
     * Set new stem
     * @param theStemInfo stem info
     * @param theHeadInfo head info
     * @param thePlacementType placement type
     * @param theInsertInfo insert info
     */
    setStem(theStemInfo, theHeadInfo, thePlacementType, theInsertInfo = { scale: 1.0, rotationPoint: { x: 0, y: 0, z: 0 } }) {
        if (!StemHeadUtilities.isValidHead(theHeadInfo) || !StemHeadUtilities.isValidStem(theStemInfo)
            || !StemHeadUtilities.isValidInsert(theInsertInfo)) {
            console.error("Error: setStem - implant data are not valid");
            return;
        }
        const aCurCtx = this.context.current;
        const anEngCtx = this.context.engineering;
        const anOldStemScale = anEngCtx.stemInfo.scale;
        const aNewStemScale = theStemInfo.scale;
        const oldStemInfo = anEngCtx.stemInfo;
        const oldHeadInfo = anEngCtx.headInfo;
        anEngCtx.stemInfo = theStemInfo;
        const isValidOldStem = StemHeadUtilities.isValidStem(oldStemInfo);
        //! rescale stem
        //! Initially all transformations for the mm eg 1.0
        //! But if we have different scale we should rescale all transformations
        const rescale = (theTrsf) => {
            const aTrsf = Mat4Tools.multiply(theTrsf, Mat4Tools.scale(aNewStemScale / anOldStemScale));
            return aTrsf;
        };
        if (anOldStemScale != aNewStemScale) {
            aCurCtx.transform.stemTGlobal = rescale(aCurCtx.transform.stemTGlobal);
            anEngCtx.transform.stemTGlobal = rescale(anEngCtx.transform.stemTGlobal);
            aCurCtx.customTransform.adjustViewStem = rescale(aCurCtx.customTransform.adjustViewStem);
        }
        // Revert the insert COR offset such that head and stem matrices are calculated around the liner COR.
        const isValidOldInsert = StemHeadUtilities.isValidInsert(anEngCtx.insertInfo);
        if (isValidOldInsert && !Vec3Tools.isZero(anEngCtx.insertInfo.rotationPoint)) {
            let aRelativeTrans = (theInsertTrsf) => {
                const anUnscaledInsertTrsf = Mat4Tools.unscale(theInsertTrsf);
                const anOldRotationPnt = Vec3Tools.applyMatrix(anUnscaledInsertTrsf, anEngCtx.insertInfo.rotationPoint);
                const anNewRotationPnt = Vec3Tools.applyMatrix(anUnscaledInsertTrsf, { x: 0, y: 0, z: 0 });
                return Mat4Tools.translation(Vec3Tools.subtract(anNewRotationPnt, anOldRotationPnt));
            };
            let aTransform = (theOldTrsf, theInsertTrsf) => {
                return Mat4Tools.multiply(aRelativeTrans(theInsertTrsf), theOldTrsf);
            };
            // Adjust Stem with Insert COR offset
            anEngCtx.transform.stemTGlobal = aTransform(anEngCtx.transform.stemTGlobal, anEngCtx.transform.insertTGlobal);
            aCurCtx.transform.stemTGlobal = aTransform(aCurCtx.transform.stemTGlobal, aCurCtx.transform.insertTGlobal);
            aCurCtx.customTransform.adjustViewStem =
                aTransform(aCurCtx.customTransform.adjustViewStem, aCurCtx.customTransform.adjustViewInsert);
            // Adjust Femur with Insert COR offset
            aCurCtx.customTransform.adjustViewFemur =
                aTransform(aCurCtx.customTransform.adjustViewFemur, aCurCtx.customTransform.adjustViewInsert);
            if (anEngCtx.operationSide == OperationSide.Right) {
                anEngCtx.transform.femurRightTGlobal = aTransform(anEngCtx.transform.femurRightTGlobal, anEngCtx.transform.insertTGlobal);
                aCurCtx.transform.femurRightTGlobal = aTransform(aCurCtx.transform.femurRightTGlobal, aCurCtx.transform.insertTGlobal);
            }
            else {
                anEngCtx.transform.femurLeftTGlobal = aTransform(anEngCtx.transform.femurLeftTGlobal, anEngCtx.transform.insertTGlobal);
                aCurCtx.transform.femurLeftTGlobal = aTransform(aCurCtx.transform.femurLeftTGlobal, aCurCtx.transform.insertTGlobal);
            }
            this.updateFemurEngineeringLandmarks(aRelativeTrans(anEngCtx.transform.insertTGlobal));
        }
        //! If stem is valid we should recalculate transformations to keep the head in the same position
        //! and move the femur respect to the stem
        if (isValidOldStem) {
            //! Get relative stem transform
            //! New stem head position should be in the same position as old stem head position
            let [aRelativeStemHeadTransform, aRelativeEndTransform] = StemHeadUtilities.computeStemRelativeTransform(theStemInfo, theHeadInfo, oldStemInfo, oldHeadInfo, aCurCtx.customTransform.adjustViewStem);
            let [aRelativeInitStemHeadTransform, aRelativeInitEndTransform] = StemHeadUtilities.computeStemRelativeTransform(theStemInfo, theHeadInfo, oldStemInfo, oldHeadInfo, anEngCtx.transform.stemTGlobal);
            let aRelativeInitTrans = (theOldTrsf, theOffset = aRelativeInitStemHeadTransform) => {
                return Mat4Tools.multiply(theOffset, theOldTrsf);
            };
            anEngCtx.transform.stemTGlobal = aRelativeInitTrans(anEngCtx.transform.stemTGlobal);
            //! For the offset mode we should put femur in the same relative position respect to the stem
            //! end point as it was before
            //! For other modes we should put femur in the same relative position respect to the stem neck
            //! point as it was before
            let aRelativeFemurTransform = thePlacementType == StemPlacementType.Offset
                ? aRelativeEndTransform
                : aRelativeStemHeadTransform;
            let aRelativeTrans = (theOldTrsf, theOffset = aRelativeStemHeadTransform) => {
                return Mat4Tools.multiply(theOffset, theOldTrsf);
            };
            aCurCtx.customTransform.adjustViewStem = aRelativeTrans(aCurCtx.customTransform.adjustViewStem);
            aCurCtx.transform.stemTGlobal = aRelativeTrans(aCurCtx.transform.stemTGlobal);
            aCurCtx.customTransform.adjustViewFemur =
                aRelativeTrans(aCurCtx.customTransform.adjustViewFemur, aRelativeFemurTransform);
            //! move femur
            if (anEngCtx.operationSide == OperationSide.Right) {
                anEngCtx.transform.femurRightTGlobal = aRelativeTrans(anEngCtx.transform.femurRightTGlobal, aRelativeFemurTransform);
                aCurCtx.transform.femurRightTGlobal = aRelativeTrans(aCurCtx.transform.femurRightTGlobal, aRelativeFemurTransform);
            }
            else {
                anEngCtx.transform.femurLeftTGlobal = aRelativeTrans(anEngCtx.transform.femurLeftTGlobal, aRelativeFemurTransform);
                aCurCtx.transform.femurLeftTGlobal = aRelativeTrans(aCurCtx.transform.femurLeftTGlobal, aRelativeFemurTransform);
            }
            this.updateFemurEngineeringLandmarks(aRelativeFemurTransform);
        }
        let isCTFreeInitialization = false;
        //! Set initial stem transformation for CT free mode
        if (anEngCtx.computedTomographyMode == CTMode.CTFree && !isValidOldStem) {
            const anOperationSide = anEngCtx.operationSide;
            let aPelvisCOR = anOperationSide == OperationSide.Right
                ? anEngCtx.landmarks.pelvisRightCorLandmark
                : anEngCtx.landmarks.pelvisLeftCorLandmark;
            // Liner COR and Insert COR must be applied on CT-Free initial assembly.
            if (LinerCupUtilities.isValidLiner(anEngCtx.linerInfo)
                && !Vec3Tools.isZero(anEngCtx.linerInfo.rotationPoint)) {
                aPelvisCOR = this.linerCenter;
            }
            isCTFreeInitialization = true;
            const aCS = {
                location: { x: 0, y: 0, z: 0 },
                xDirection: { x: 0, y: anOperationSide == OperationSide.Right ? 1 : -1, z: 0 },
                direction: { x: 0, y: 0, z: -1 }
            };
            const aRotation = Mat4Tools.getCSTransformation(aCS);
            const aStemScale = Mat4Tools.scale(theStemInfo.scale);
            let aStemTransformation = Mat4Tools.multiply(aRotation, aStemScale);
            const headTransform = StemHeadUtilities.computeHeadTransform(theHeadInfo, theStemInfo, aStemTransformation);
            const aHeadPosition = Mat4Tools.getTranslation(headTransform);
            const aTranslation = Mat4Tools.translation(Vec3Tools.subtract(aPelvisCOR, aHeadPosition));
            const aStemRotationScale = Mat4Tools.getRotationScale(aStemTransformation);
            const anInitialStemTransform = Mat4Tools.multiply(aTranslation, aStemRotationScale);
            aStemTransformation = Mat4Tools.multiply(aTranslation, aStemTransformation);
            aCurCtx.transform.stemTGlobal = anInitialStemTransform;
            anEngCtx.transform.stemTGlobal = anInitialStemTransform;
            aCurCtx.customTransform.adjustViewStem = anInitialStemTransform;
        }
        //! set head transformation
        anEngCtx.transform.headTGlobal =
            StemHeadUtilities.computeHeadTransform(theHeadInfo, theStemInfo, anEngCtx.transform.stemTGlobal);
        aCurCtx.transform.headTGlobal =
            StemHeadUtilities.computeHeadTransform(theHeadInfo, theStemInfo, aCurCtx.transform.stemTGlobal);
        aCurCtx.customTransform.adjustViewHead =
            StemHeadUtilities.computeHeadTransform(theHeadInfo, theStemInfo, aCurCtx.customTransform.adjustViewStem);
        anEngCtx.headInfo = theHeadInfo;
        //! set insert transformation
        const anInsertScale = Mat4Tools.scale(theInsertInfo.scale);
        anEngCtx.transform.insertTGlobal =
            Mat4Tools.multiply(Mat4Tools.unscale(anEngCtx.transform.headTGlobal), anInsertScale);
        aCurCtx.transform.insertTGlobal =
            Mat4Tools.multiply(Mat4Tools.unscale(aCurCtx.transform.headTGlobal), anInsertScale);
        aCurCtx.customTransform.adjustViewInsert =
            Mat4Tools.multiply(Mat4Tools.unscale(aCurCtx.customTransform.adjustViewHead), anInsertScale);
        // Apply insert offset to stem/head/femur, such that they are aligned with insert COR
        if ((isCTFreeInitialization || isValidOldInsert)
            && !Vec3Tools.isZero(theInsertInfo.rotationPoint)) {
            let aRelativeTrans = (theInsertTrsf) => {
                const anUnscaledInsertTrsf = Mat4Tools.unscale(theInsertTrsf);
                const anOldRotationPnt = Vec3Tools.applyMatrix(anUnscaledInsertTrsf, { x: 0, y: 0, z: 0 });
                const anNewRotationPnt = Vec3Tools.applyMatrix(anUnscaledInsertTrsf, theInsertInfo.rotationPoint);
                return Mat4Tools.translation(Vec3Tools.subtract(anNewRotationPnt, anOldRotationPnt));
            };
            let aTransform = (theOldTrsf, theInsertTrsf) => {
                return Mat4Tools.multiply(aRelativeTrans(theInsertTrsf), theOldTrsf);
            };
            // Adjust Stem with Insert COR offset
            anEngCtx.transform.stemTGlobal = aTransform(anEngCtx.transform.stemTGlobal, anEngCtx.transform.insertTGlobal);
            aCurCtx.transform.stemTGlobal = aTransform(aCurCtx.transform.stemTGlobal, aCurCtx.transform.insertTGlobal);
            aCurCtx.customTransform.adjustViewStem =
                aTransform(aCurCtx.customTransform.adjustViewStem, aCurCtx.customTransform.adjustViewInsert);
            // Adjust Head with Insert COR offset
            anEngCtx.transform.headTGlobal = aTransform(anEngCtx.transform.headTGlobal, anEngCtx.transform.insertTGlobal);
            aCurCtx.transform.headTGlobal = aTransform(aCurCtx.transform.headTGlobal, aCurCtx.transform.insertTGlobal);
            aCurCtx.customTransform.adjustViewHead =
                aTransform(aCurCtx.customTransform.adjustViewHead, aCurCtx.customTransform.adjustViewInsert);
            // Adjust Femur with Insert COR offset
            aCurCtx.customTransform.adjustViewFemur =
                aTransform(aCurCtx.customTransform.adjustViewFemur, aCurCtx.customTransform.adjustViewInsert);
            if (anEngCtx.operationSide == OperationSide.Right) {
                anEngCtx.transform.femurRightTGlobal = aTransform(anEngCtx.transform.femurRightTGlobal, anEngCtx.transform.insertTGlobal);
                aCurCtx.transform.femurRightTGlobal = aTransform(aCurCtx.transform.femurRightTGlobal, aCurCtx.transform.insertTGlobal);
            }
            else {
                anEngCtx.transform.femurLeftTGlobal = aTransform(anEngCtx.transform.femurLeftTGlobal, anEngCtx.transform.insertTGlobal);
                aCurCtx.transform.femurLeftTGlobal = aTransform(aCurCtx.transform.femurLeftTGlobal, aCurCtx.transform.insertTGlobal);
            }
            this.updateFemurEngineeringLandmarks(aRelativeTrans(anEngCtx.transform.insertTGlobal));
        }
        anEngCtx.insertInfo = theInsertInfo;
        if (isValidOldStem) {
            this.adjustPlanMoveStem();
        }
    }
    /**
     * Adjusts plan move stem.
     * Will update femur and stem transformations.
     * Using landmarks, initial transformations and cup position.
     * Also use custom stem transformation for adjusting view.
     */
    adjustPlanMoveStem() {
        const aCtx = this.context;
        //! compute head transformation
        aCtx.current.customTransform.adjustViewHead =
            StemHeadUtilities.computeHeadTransform(aCtx.engineering.headInfo, aCtx.engineering.stemInfo, aCtx.current.customTransform.adjustViewStem);
        const anInsertScale = Mat4Tools.scale(aCtx.engineering.insertInfo.scale);
        aCtx.current.customTransform.adjustViewInsert =
            Mat4Tools.multiply(Mat4Tools.unscale(aCtx.current.customTransform.adjustViewHead), anInsertScale);
        const anEngineeringLinerCor = Vec3Tools.applyMatrix(Mat4Tools.unscale(aCtx.engineering.transform.linerTGlobal), aCtx.engineering.linerInfo.rotationPoint);
        const cupTranslate = Mat4Tools.translation(Vec3Tools.subtract(this.linerCenter, anEngineeringLinerCor));
        const applyCupTranslation = (theTrsf) => {
            const aTrsf = Mat4Tools.multiply(cupTranslate, theTrsf);
            return aTrsf;
        };
        const operationSide = aCtx.engineering.operationSide;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            const aRelativeRotation = Mat4Tools.relativeTransform(Mat4Tools.getRotation(aCtx.engineering.transform.stemTGlobal), Mat4Tools.getRotation(aCtx.current.customTransform.adjustViewStem));
            //! we should rotate around the liner COR
            const aCorTransform = Mat4Tools.translation(anEngineeringLinerCor);
            let aRelativeTransform = Mat4Tools.multiply(aCorTransform, Mat4Tools.multiply(aRelativeRotation, Mat4Tools.invert(aCorTransform)));
            aCtx.current.transform.stemTGlobal =
                Mat4Tools.multiply(aRelativeTransform, aCtx.engineering.transform.stemTGlobal);
            aCtx.current.transform.headTGlobal =
                Mat4Tools.multiply(aRelativeTransform, aCtx.engineering.transform.headTGlobal);
            aCtx.current.transform.insertTGlobal =
                Mat4Tools.multiply(aRelativeTransform, aCtx.engineering.transform.insertTGlobal);
            //! apply cup translation to stem
            aCtx.current.transform.stemTGlobal = applyCupTranslation(aCtx.current.transform.stemTGlobal);
            //! apply cup translation to head
            aCtx.current.transform.headTGlobal = applyCupTranslation(aCtx.current.transform.headTGlobal);
            //! apply cup translation to insert
            aCtx.current.transform.insertTGlobal = applyCupTranslation(aCtx.current.transform.insertTGlobal);
            return;
        }
        const Transform12 = aCtx.current.customTransform.adjustViewStem;
        const landmark1Initial = operationSide == OperationSide.Right
            ? aCtx.engineering.landmarks.femurRightCondyleLatLandmark
            : aCtx.engineering.landmarks.femurLeftCondyleLatLandmark;
        const landmark2Initial = operationSide == OperationSide.Right
            ? aCtx.engineering.landmarks.femurRightCondyleMedLandmark
            : aCtx.engineering.landmarks.femurLeftCondyleMedLandmark;
        const aLinerCOR = anEngineeringLinerCor;
        const femurInitialTransform = operationSide == OperationSide.Right
            ? aCtx.engineering.transform.femurRightTGlobal
            : aCtx.engineering.transform.femurLeftTGlobal;
        // ! Transform12 = dt * Transform1
        // ! dt = Transform12 * Transform1^-1
        // ! dt^-1 = Transform1 * Transform12^-1
        // ! femurTransform = dt^-1 * InitialTransform2
        let dtInvert = Mat4Tools.multiply(aCtx.engineering.transform.stemTGlobal, Mat4Tools.invert(Transform12));
        const landmark1 = Vec3Tools.applyMatrix(dtInvert, landmark1Initial);
        const landmark2 = Vec3Tools.applyMatrix(dtInvert, landmark2Initial);
        // ! center of coordinate system = (landmark1 + landmark2) / 2
        const center = Vec3Tools.divide(Vec3Tools.add(landmark1, landmark2), 2);
        let oX = Vec3Tools.subtract(landmark2, landmark1);
        const oZ = Vec3Tools.subtract(aLinerCOR, center);
        const oY = Vec3Tools.cross(Vec3Tools.normalize(oZ), Vec3Tools.normalize(oX));
        oX = Vec3Tools.cross(Vec3Tools.normalize(oY), Vec3Tools.normalize(oZ));
        if (this.context.engineering.operationSide == OperationSide.Left) {
            oX = Vec3Tools.reverse(oX);
        }
        const postOpCS = {
            location: { x: 0, y: 0, z: 0 },
            xDirection: oX,
            direction: oZ,
        };
        const postOPSystem = Mat4Tools.getCSTransformation(postOpCS);
        //! step 2
        //! We need to build transformation matrix which will rotate post-op coordinate system
        //! to align with pre-op coordinate system
        //! Around head origin and then apply this transformation to stem and femur
        //! pre-op coordinate system = initialCoordinateSystem
        //! post-op coordinate system = postOPSystem
        //! initialCoordinateSystem = rotation2 * postOPSystem
        //! rotation2 = initialCoordinateSystem * postOPSystem ^-1
        const aLinerCorTranslationMat = Mat4Tools.translation(aLinerCOR);
        const oppTransformation = Mat4Tools.multiply(aLinerCorTranslationMat, Mat4Tools.multiply(postOPSystem, Mat4Tools.invert(aLinerCorTranslationMat)));
        let aFemurTransform = Mat4Tools.multiply(oppTransformation, Mat4Tools.multiply(dtInvert, femurInitialTransform));
        //! apply cap translation to femur
        aFemurTransform = applyCupTranslation(aFemurTransform);
        if (operationSide == OperationSide.Right) {
            aCtx.current.transform.femurRightTGlobal = aFemurTransform;
        }
        else {
            aCtx.current.transform.femurLeftTGlobal = aFemurTransform;
        }
        aCtx.current.transform.stemTGlobal = Mat4Tools.multiply(oppTransformation, aCtx.engineering.transform.stemTGlobal);
        aCtx.current.transform.headTGlobal = Mat4Tools.multiply(oppTransformation, aCtx.engineering.transform.headTGlobal);
        aCtx.current.transform.insertTGlobal =
            Mat4Tools.multiply(oppTransformation, aCtx.engineering.transform.insertTGlobal);
        //! apply cup translation to stem
        aCtx.current.transform.stemTGlobal = applyCupTranslation(aCtx.current.transform.stemTGlobal);
        //! apply cup translation to head
        aCtx.current.transform.headTGlobal = applyCupTranslation(aCtx.current.transform.headTGlobal);
        //! apply cup translation to insert
        aCtx.current.transform.insertTGlobal = applyCupTranslation(aCtx.current.transform.insertTGlobal);
        //! update landmarks
        this.updateLandmarks();
    }
    /**
     * Update femur engineering landmarks
     * We use it when we change femur engineering transformation
     */
    updateFemurEngineeringLandmarks(theFemurTransform) {
        // TODO: Remove this function, create logic without changing engineering plan
        const anEngCtx = this.context.engineering;
        if (anEngCtx.computedTomographyMode == CTMode.CTFree) {
            return;
        }
        const ul = (theLandmark) => {
            return Vec3Tools.applyMatrix(theFemurTransform, theLandmark);
        };
        const landmarks = anEngCtx.landmarks;
        if (anEngCtx.operationSide == OperationSide.Right) {
            landmarks.femurRightCorLandmark = ul(landmarks.femurRightCorLandmark);
            landmarks.femurRightLtLandmark = ul(landmarks.femurRightLtLandmark);
            landmarks.femurRightLtProxLandmark = ul(landmarks.femurRightLtProxLandmark);
            landmarks.femurRightGtLandmark = ul(landmarks.femurRightGtLandmark);
            landmarks.femurRightCondyleMedLandmark = ul(landmarks.femurRightCondyleMedLandmark);
            landmarks.femurRightCondyleLatLandmark = ul(landmarks.femurRightCondyleLatLandmark);
            landmarks.femurRightAxisStartLandmark = ul(landmarks.femurRightAxisStartLandmark);
            landmarks.femurRightAxisEndLandmark = ul(landmarks.femurRightAxisEndLandmark);
        }
        else {
            landmarks.femurLeftCorLandmark = ul(landmarks.femurLeftCorLandmark);
            landmarks.femurLeftLtLandmark = ul(landmarks.femurLeftLtLandmark);
            landmarks.femurLeftLtProxLandmark = ul(landmarks.femurLeftLtProxLandmark);
            landmarks.femurLeftGtLandmark = ul(landmarks.femurLeftGtLandmark);
            landmarks.femurLeftCondyleMedLandmark = ul(landmarks.femurLeftCondyleMedLandmark);
            landmarks.femurLeftCondyleLatLandmark = ul(landmarks.femurLeftCondyleLatLandmark);
            landmarks.femurLeftAxisStartLandmark = ul(landmarks.femurLeftAxisStartLandmark);
            landmarks.femurLeftAxisEndLandmark = ul(landmarks.femurLeftAxisEndLandmark);
        }
    }
    /**
     * Update landmarks
     */
    updateLandmarks() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return;
        }
        const operationSide = aCtx.engineering.operationSide;
        const femurTransformCurrent = operationSide == OperationSide.Right
            ? aCtx.current.transform.femurRightTGlobal
            : aCtx.current.transform.femurLeftTGlobal;
        //! engeneering transform cup
        const femurTransformationEngineering = operationSide == OperationSide.Right
            ? aCtx.engineering.transform.femurRightTGlobal
            : aCtx.engineering.transform.femurLeftTGlobal;
        //! femur transformation:
        //! femurTransform = femurTransformCurrent * femurTransformationEngineering^-1
        const femurTransform = Mat4Tools.multiply(femurTransformCurrent, Mat4Tools.invert(femurTransformationEngineering));
        //! get landmarks
        const landmarks = aCtx.engineering.landmarks;
        const ul = (theLandmark) => {
            return Vec3Tools.applyMatrix(femurTransform, theLandmark);
        };
        if (operationSide == OperationSide.Right) {
            aCtx.current.landmarks.femurRightCorLandmark = ul(landmarks.femurRightCorLandmark);
            aCtx.current.landmarks.femurRightLtLandmark = ul(landmarks.femurRightLtLandmark);
            aCtx.current.landmarks.femurRightLtProxLandmark = ul(landmarks.femurRightLtProxLandmark);
            aCtx.current.landmarks.femurRightGtLandmark = ul(landmarks.femurRightGtLandmark);
            aCtx.current.landmarks.femurRightCondyleMedLandmark = ul(landmarks.femurRightCondyleMedLandmark);
            aCtx.current.landmarks.femurRightCondyleLatLandmark = ul(landmarks.femurRightCondyleLatLandmark);
            aCtx.current.landmarks.femurRightAxisStartLandmark = ul(landmarks.femurRightAxisStartLandmark);
            aCtx.current.landmarks.femurRightAxisEndLandmark = ul(landmarks.femurRightAxisEndLandmark);
        }
        else {
            aCtx.current.landmarks.femurLeftCorLandmark = ul(landmarks.femurLeftCorLandmark);
            aCtx.current.landmarks.femurLeftLtLandmark = ul(landmarks.femurLeftLtLandmark);
            aCtx.current.landmarks.femurLeftLtProxLandmark = ul(landmarks.femurLeftLtProxLandmark);
            aCtx.current.landmarks.femurLeftGtLandmark = ul(landmarks.femurLeftGtLandmark);
            aCtx.current.landmarks.femurLeftCondyleMedLandmark = ul(landmarks.femurLeftCondyleMedLandmark);
            aCtx.current.landmarks.femurLeftCondyleLatLandmark = ul(landmarks.femurLeftCondyleLatLandmark);
            aCtx.current.landmarks.femurLeftAxisStartLandmark = ul(landmarks.femurLeftAxisStartLandmark);
            aCtx.current.landmarks.femurLeftAxisEndLandmark = ul(landmarks.femurLeftAxisEndLandmark);
        }
    }
    /**
     * Leg Length - The distance measured between the medial-lateral axis of
     * the pelvis (the line connecting the two ASIS landmarks) and the lesser
     * trochanter when measured in the coronal plane.
     */
    get legLengthPostop() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const aLtLandmark = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightLtLandmark
            : aCtx.current.landmarks.femurLeftLtLandmark;
        const aLegLengthPostop = PatientUtils._myMedicalUtils.calculateLegLength(aCtx.current.landmarks.pelvisLeftAsisLandmark, aCtx.current.landmarks.pelvisRightAsisLandmark, aLtLandmark);
        return aLegLengthPostop;
    }
    /**
     * Leg Length Preop - The distance measured between the medial-lateral axis of
     * the pelvis (the line connecting the two ASIS landmarks) and the lesser
     * trochanter when measured in the coronal plane.
     */
    get legLengthPreop() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const aLtLandmark = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightLtLandmark
            : aCtx.current.landmarks.femurLeftLtLandmark;
        const aTransform = aCtx.current.transform;
        const aFemurPostopToPreopTrsf = anOperationSide == OperationSide.Right
            ? Mat4Tools.multiply(aTransform.femurRightPreopTGlobal, Mat4Tools.invert(aTransform.femurRightTGlobal))
            : Mat4Tools.multiply(aTransform.femurLeftPreopTGlobal, Mat4Tools.invert(aTransform.femurLeftTGlobal));
        const aFemurLtPreop = Vec3Tools.applyMatrix(aFemurPostopToPreopTrsf, aLtLandmark);
        return PatientUtils._myMedicalUtils.calculateLegLength(aCtx.current.landmarks.pelvisLeftAsisLandmark, aCtx.current.landmarks.pelvisRightAsisLandmark, aFemurLtPreop);
    }
    /**
     * Leg Length Opposite - The distance measured between the medial-lateral axis of
     * the pelvis (the line connecting the two ASIS landmarks) and the lesser
     * trochanter when measured in the coronal plane.
     */
    get legLengthOpposite() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const aLtLandmark = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurLeftLtLandmark
            : aCtx.current.landmarks.femurRightLtLandmark;
        return PatientUtils._myMedicalUtils.calculateLegLength(aCtx.current.landmarks.pelvisLeftAsisLandmark, aCtx.current.landmarks.pelvisRightAsisLandmark, aLtLandmark);
    }
    /**
     * Leg Length change for Postop vs Preop in mm
     */
    get legLengthVsPreOp() { return this.legLengthPostop - this.legLengthPreop; }
    /**
     * Leg Length change for Postop vs non-operative leg in mm
     */
    get legLengthVsOpposite() { return this.legLengthPostop - this.legLengthOpposite; }
    /**
     * Femoral Offset Postop in mm.
     * The distance between the femur canal axis and the head
     * center of rotation.
     */
    get femoralOffsetPostop() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const anAxisStart = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightAxisStartLandmark
            : aCtx.current.landmarks.femurLeftAxisStartLandmark;
        const anAxisEnd = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightAxisEndLandmark
            : aCtx.current.landmarks.femurLeftAxisEndLandmark;
        const aHeadCOR = this.linerCenter;
        return PatientUtils._myMedicalUtils.calculateLegFemoralOffset(anAxisStart, anAxisEnd, aHeadCOR);
    }
    /**
     * Femoral Offset Preop in mm
     * The distance between the femur canal axis and the native
     * femur center of rotation.
     */
    get femoralOffsetPreop() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const anAxisStart = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightAxisStartLandmark
            : aCtx.current.landmarks.femurLeftAxisStartLandmark;
        const anAxisEnd = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightAxisEndLandmark
            : aCtx.current.landmarks.femurLeftAxisEndLandmark;
        const aFemurCOR = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightCorLandmark
            : aCtx.current.landmarks.femurLeftCorLandmark;
        return PatientUtils._myMedicalUtils.calculateLegFemoralOffset(anAxisStart, anAxisEnd, aFemurCOR);
    }
    /**
     * Femoral Offset Opposite in mm
     * The distance between the femur canal axis and the native
     * femur center of rotation.
     */
    get femoralOffsetOpposite() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const anAxisStart = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurLeftAxisStartLandmark
            : aCtx.current.landmarks.femurRightAxisStartLandmark;
        const anAxisEnd = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurLeftAxisEndLandmark
            : aCtx.current.landmarks.femurRightAxisEndLandmark;
        const aFemurCOR = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurLeftCorLandmark
            : aCtx.current.landmarks.femurRightCorLandmark;
        return PatientUtils._myMedicalUtils.calculateLegFemoralOffset(anAxisStart, anAxisEnd, aFemurCOR);
    }
    /**
     * Acetabular Offset Postop in mm.
     * The distance between the mid sagittal plane and the liner
     * center of rotation of the femur.
     */
    get acetabularOffsetPostop() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const aLinerCOR = this.linerCenter;
        return PatientUtils._myMedicalUtils.calculateLegAcetabularOffset(aLinerCOR);
    }
    /**
     * Acetabular Offset Preop in mm.
     * The distance between the mid sagittal plane and the native
     * acetabulum center of rotation of the femur.
     */
    get acetabularOffsetPreop() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const aTransform = aCtx.current.transform;
        const aFemurPostopToPreopTrsf = anOperationSide == OperationSide.Right
            ? Mat4Tools.multiply(aTransform.femurRightPreopTGlobal, Mat4Tools.invert(aTransform.femurRightTGlobal))
            : Mat4Tools.multiply(aTransform.femurLeftPreopTGlobal, Mat4Tools.invert(aTransform.femurLeftTGlobal));
        const aFemurCOR = anOperationSide == OperationSide.Right
            ? Vec3Tools.applyMatrix(aFemurPostopToPreopTrsf, aCtx.current.landmarks.femurRightCorLandmark)
            : Vec3Tools.applyMatrix(aFemurPostopToPreopTrsf, aCtx.current.landmarks.femurLeftCorLandmark);
        return PatientUtils._myMedicalUtils.calculateLegAcetabularOffset(aFemurCOR);
    }
    /**
     * Acetabular Offset Opposite in mm.
     * The distance between the mid sagittal plane and the native
     * acetabulum center of rotation of the femur.
     */
    get acetabularOffsetOpposite() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const aFemurCOR = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurLeftCorLandmark
            : aCtx.current.landmarks.femurRightCorLandmark;
        return PatientUtils._myMedicalUtils.calculateLegAcetabularOffset(aFemurCOR);
    }
    /**
     * Global Offset Postop
     * Femoral Offset Postop + Acetabular Offset Postop
     */
    get globalOffsetPostop() {
        return this.femoralOffsetPostop + this.acetabularOffsetPostop;
    }
    /**
     * Global Offset Preop
     * Femoral Offset Preop + Acetabular Offset Preop
     */
    get globalOffsetPreop() {
        return this.femoralOffsetPreop + this.acetabularOffsetPreop;
    }
    /**
     * Global Offset Opposite
     * Femoral Offset Opposite + Acetabular Offset Opposite
     */
    get globalOffsetOpposite() {
        return this.femoralOffsetOpposite + this.acetabularOffsetOpposite;
    }
    /**
     * Leg Offset change for Postop vs Preop in mm
     */
    get legOffsetVsPreOp() { return this.globalOffsetPostop - this.globalOffsetPreop; }
    /**
     * Leg Offset change for Postop vs non-operative leg in mm
     */
    get legOffsetVsOpposite() {
        return this.globalOffsetPostop - this.globalOffsetOpposite;
    }
    /**
     * Cup Inclination in degrees
     */
    cupInclination() {
        const aCtx = this.context;
        const aCupTrsf = aCtx.current.transform.shellTGlobal;
        const aCupAxes = {
            location: { x: 0, y: 0, z: 0 },
            xDirection: Vec3Tools.applyMatrix(Mat4Tools.getRotation(aCupTrsf), { x: 1.0, y: 0.0, z: 0.0 }),
            direction: Vec3Tools.applyMatrix(Mat4Tools.getRotation(aCupTrsf), { x: 0.0, y: 0.0, z: 1.0 })
        };
        let aCupInclination;
        if (aCtx.current.selectionOption == SelectionOption.APP) {
            aCupInclination = PatientUtils._myMedicalUtils.calculateCupInclination(aCupAxes, aCtx.current.landmarks.pelvisLeftAsisLandmark, aCtx.current.landmarks.pelvisRightAsisLandmark, aCtx.current.landmarks.pelvisLeftPubicSymphysisLandmark, aCtx.current.landmarks.pelvisRightPubicSymphysisLandmark);
        }
        else {
            aCupInclination =
                PatientUtils._myMedicalUtils.calculateCupInclination(aCupAxes, { x: 1.0, y: 0.0, z: 0.0 }, { x: -1.0, y: 0.0, z: 0.0 }, { x: 1.0, y: 0.0, z: -1.0 }, { x: -1.0, y: 0.0, z: -1.0 });
        }
        return aCtx.engineering.operationSide == OperationSide.Right ? -aCupInclination
            : aCupInclination;
    }
    /**
     * Cup Version in degrees
     */
    cupVersion() {
        const aCtx = this.context;
        const aCupTrsf = aCtx.current.transform.shellTGlobal;
        const aCupAxes = {
            location: { x: 0, y: 0, z: 0 },
            xDirection: Vec3Tools.applyMatrix(Mat4Tools.getRotation(aCupTrsf), { x: 1.0, y: 0.0, z: 0.0 }),
            direction: Vec3Tools.applyMatrix(Mat4Tools.getRotation(aCupTrsf), { x: 0.0, y: 0.0, z: 1.0 })
        };
        let aCupVersion;
        if (aCtx.current.selectionOption == SelectionOption.APP) {
            aCupVersion = PatientUtils._myMedicalUtils.calculateCupVersion(aCupAxes, aCtx.current.landmarks.pelvisLeftAsisLandmark, aCtx.current.landmarks.pelvisRightAsisLandmark, aCtx.current.landmarks.pelvisLeftPubicSymphysisLandmark, aCtx.current.landmarks.pelvisRightPubicSymphysisLandmark);
        }
        else {
            aCupVersion = PatientUtils._myMedicalUtils.calculateCupVersion(aCupAxes, { x: 1.0, y: 0.0, z: 0.0 }, { x: -1.0, y: 0.0, z: 0.0 }, { x: 1.0, y: 0.0, z: -1.0 }, { x: -1.0, y: 0.0, z: -1.0 });
        }
        return aCupVersion;
    }
    /**
     * Cup Orientation in degrees
     */
    cupOrientation() {
        const aCtx = this.context;
        const anOrientationPhase = aCtx.engineering.operationSide == OperationSide.Left ? Math.PI : 0;
        const aCupTrsf = aCtx.current.transform.shellTGlobal;
        const aCupAxes = {
            location: { x: 0, y: 0, z: 0 },
            xDirection: Vec3Tools.applyMatrix(Mat4Tools.getRotation(aCupTrsf), { x: 1.0, y: 0.0, z: 0.0 }),
            direction: Vec3Tools.applyMatrix(Mat4Tools.getRotation(aCupTrsf), { x: 0.0, y: 0.0, z: 1.0 })
        };
        let aCupOrientation;
        if (aCtx.current.selectionOption == SelectionOption.APP) {
            aCupOrientation = PatientUtils._myMedicalUtils.calculateCupOrientation(aCupAxes, aCtx.current.landmarks.pelvisLeftAsisLandmark, aCtx.current.landmarks.pelvisRightAsisLandmark, aCtx.current.landmarks.pelvisLeftPubicSymphysisLandmark, aCtx.current.landmarks.pelvisRightPubicSymphysisLandmark);
        }
        else {
            aCupOrientation =
                PatientUtils._myMedicalUtils.calculateCupOrientation(aCupAxes, { x: 1.0, y: 0.0, z: 0.0 }, { x: -1.0, y: 0.0, z: 0.0 }, { x: 1.0, y: 0.0, z: -1.0 }, { x: -1.0, y: 0.0, z: -1.0 });
        }
        aCupOrientation -= anOrientationPhase;
        return PatientUtils._myMedicalUtils.polishAngleRadians(aCupOrientation);
    }
    /**
     * Calculate new cup transformation
     * @param theCupInclination cup inclination
     * @param theCupVersion cup version
     * @param theCupTransform cup transformation
     * @returns {WasmMatrix4d} new cup transformation
     */
    cupTransformation(theCupInclination, theCupVersion, theCupOrientation, theCupTransform) {
        const aCtx = this.context;
        const anOperationSide = aCtx.engineering.operationSide;
        const aCupInclination = anOperationSide == OperationSide.Right ? -theCupInclination
            : theCupInclination;
        let aCupRotation;
        if (aCtx.current.selectionOption == SelectionOption.APP) {
            aCupRotation = PatientUtils._myMedicalUtils.calculateCupRotation(aCupInclination, theCupVersion, theCupOrientation, aCtx.current.landmarks.pelvisLeftAsisLandmark, aCtx.current.landmarks.pelvisRightAsisLandmark, aCtx.current.landmarks.pelvisLeftPubicSymphysisLandmark, aCtx.current.landmarks.pelvisRightPubicSymphysisLandmark);
        }
        else {
            aCupRotation = PatientUtils._myMedicalUtils.calculateCupRotation(aCupInclination, theCupVersion, theCupOrientation, { x: 1.0, y: 0.0, z: 0.0 }, { x: -1.0, y: 0.0, z: 0.0 }, { x: 1.0, y: 0.0, z: -1.0 }, { x: -1.0, y: 0.0, z: -1.0 });
        }
        const aCupScale = Mat4Tools.getScale(theCupTransform);
        const aCupTranslation = Mat4Tools.translation(Mat4Tools.getTranslation(theCupTransform));
        return Mat4Tools.multiply(aCupTranslation, Mat4Tools.multiply(aCupRotation, aCupScale));
    }
    /**
     * Set cup inclination, cup antiversion, liner orientation and update context
     */
    setCupAngles(theCupInclination, theCupAntiversion, theCupOrientation, theLinerOrientation) {
        const aCtx = this.context;
        const anOrientationPhase = aCtx.engineering.operationSide == OperationSide.Left ? Math.PI : 0;
        const aNewShellTrsf = this.cupTransformation(theCupInclination, theCupAntiversion, theCupOrientation + anOrientationPhase, aCtx.current.transform.shellTGlobal);
        aCtx.current.customTransform.adjustViewShell = aNewShellTrsf;
        aCtx.current.transform.shellTGlobal = aNewShellTrsf;
        const aNewLinerTrsf = this.cupTransformation(theCupInclination, theCupAntiversion, theCupOrientation + theLinerOrientation
            + anOrientationPhase, aCtx.current.transform.linerTGlobal);
        aCtx.current.customTransform.adjustViewLiner = aNewLinerTrsf;
        aCtx.current.transform.linerTGlobal = aNewLinerTrsf;
        this.adjustPlanMoveStem();
    }
    /**
     * Set liner orientation only.
     * Don't change the other cup angles.
     */
    setLinerOrientation(theLinerOrientation) {
        const aCtx = this.context;
        const anOrientationPhase = aCtx.engineering.operationSide == OperationSide.Left ? Math.PI : 0;
        const aNewLinerTrsf = this.cupTransformation(this.cupInclination(), this.cupVersion(), this.cupOrientation() + theLinerOrientation
            + anOrientationPhase, aCtx.current.transform.linerTGlobal);
        aCtx.current.customTransform.adjustViewLiner = aNewLinerTrsf;
        aCtx.current.transform.linerTGlobal = aNewLinerTrsf;
    }
    /**
     * Stem Version in degrees
     */
    stemVersion() {
        const aCtx = this.context;
        const aStemInfo = aCtx.engineering.stemInfo;
        if (!StemHeadUtilities.isValidStem(aStemInfo)) {
            console.error("Error: stemVersion - implant data are not valid", aStemInfo);
            return 0;
        }
        const aStemTrsf = Mat4Tools.unscale(aCtx.current.customTransform.adjustViewStem);
        const aHeadEndPoint = Vec3Tools.applyMatrix(aStemTrsf, aStemInfo.headEndPoint);
        const aStemNeck = Vec3Tools.applyMatrix(aStemTrsf, aStemInfo.stemNeckPoint);
        const anOperationSide = aCtx.engineering.operationSide;
        let aStemVersion;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTBased) {
            const aFemurCondyleLat = anOperationSide == OperationSide.Right
                ? aCtx.engineering.landmarks.femurRightCondyleLatLandmark
                : aCtx.engineering.landmarks.femurLeftCondyleLatLandmark;
            const aFemurCondyleMed = anOperationSide == OperationSide.Right
                ? aCtx.engineering.landmarks.femurRightCondyleMedLandmark
                : aCtx.engineering.landmarks.femurLeftCondyleMedLandmark;
            const aCondMidPoint = Vec3Tools.divide(Vec3Tools.add(aFemurCondyleLat, aFemurCondyleMed), 2);
            const aHeadCOR = anOperationSide == OperationSide.Right
                ? aCtx.engineering.landmarks.femurRightCorLandmark
                : aCtx.engineering.landmarks.femurLeftCorLandmark;
            const aSuperiorAxis = Vec3Tools.normalize(Vec3Tools.subtract(aHeadCOR, aCondMidPoint));
            aStemVersion = PatientUtils._myMedicalUtils.calculateStemVersion(aHeadEndPoint, aStemNeck, aFemurCondyleLat, aFemurCondyleMed, aSuperiorAxis);
        }
        else {
            aStemVersion = PatientUtils._myMedicalUtils.calculateStemVersion(aHeadEndPoint, aStemNeck, { x: 0.0, y: 0.0, z: 0.0 }, { x: anOperationSide == OperationSide.Right ? 1.0 : -1.0, y: 0.0, z: 0.0 }, { x: 0.0, y: 0.0, z: 1.0 });
        }
        return aCtx.engineering.operationSide == OperationSide.Right ? -aStemVersion : aStemVersion;
    }
    /**
     * Combined Version in degrees
     */
    combinedVersion() { return this.cupVersion() + this.stemVersion(); }
    /**
     * Set stem anteversion and undate context
     */
    setStemAnteversion(theStemAnteversion) {
        const aCtx = this.context;
        const aStemInfo = aCtx.engineering.stemInfo;
        if (!StemHeadUtilities.isValidStem(aStemInfo)) {
            console.error("Error: setStemAnteversion - implant data are not valid", aStemInfo);
            return;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const aZAxis = Vec3Tools.applyMatrix(Mat4Tools.getRotation(aCtx.current.customTransform.adjustViewStem), { x: 0, y: 0, z: 1 });
        const aStemTransform = aCtx.current.customTransform.adjustViewStem;
        let aStemCS = {
            location: { x: 0, y: 0, z: 0 },
            xDirection: Vec3Tools.cross(aZAxis, { x: anOperationSide == OperationSide.Right ? -1 : 1, y: 0, z: 0 }),
            direction: aZAxis
        };
        const aStemBaseCSTrsf = Mat4Tools.invert(Mat4Tools.getCSTransformation(aStemCS));
        const aStemTranslation = Mat4Tools.translation(Mat4Tools.getTranslation(aStemTransform));
        let aStemRotation;
        const aHeadEndPoint = Vec3Tools.applyMatrix(Mat4Tools.multiply(aStemTranslation, aStemBaseCSTrsf), aStemInfo.headEndPoint);
        const aStemNeck = Vec3Tools.applyMatrix(Mat4Tools.multiply(aStemTranslation, aStemBaseCSTrsf), aStemInfo.stemNeckPoint);
        if (aCtx.engineering.computedTomographyMode == CTMode.CTBased) {
            const aFemurCondyleLat = anOperationSide == OperationSide.Right
                ? aCtx.engineering.landmarks.femurRightCondyleLatLandmark
                : aCtx.engineering.landmarks.femurLeftCondyleLatLandmark;
            const aFemurCondyleMed = anOperationSide == OperationSide.Right
                ? aCtx.engineering.landmarks.femurRightCondyleMedLandmark
                : aCtx.engineering.landmarks.femurLeftCondyleMedLandmark;
            const aCondMidPoint = Vec3Tools.divide(Vec3Tools.add(aFemurCondyleLat, aFemurCondyleMed), 2);
            const aHeadCOR = anOperationSide == OperationSide.Right
                ? aCtx.engineering.landmarks.femurRightCorLandmark
                : aCtx.engineering.landmarks.femurLeftCorLandmark;
            const aSuperiorAxis = Vec3Tools.normalize(Vec3Tools.subtract(aHeadCOR, aCondMidPoint));
            aStemRotation = PatientUtils._myMedicalUtils.calculateStemRotation(anOperationSide == OperationSide.Right ? theStemAnteversion : -theStemAnteversion, aStemCS, aHeadEndPoint, aStemNeck, aFemurCondyleLat, aFemurCondyleMed, aSuperiorAxis);
        }
        else {
            aStemRotation = PatientUtils._myMedicalUtils.calculateStemRotation(anOperationSide == OperationSide.Right ? theStemAnteversion : -theStemAnteversion, aStemCS, aHeadEndPoint, aStemNeck, { x: 0.0, y: 0.0, z: 0.0 }, { x: anOperationSide == OperationSide.Right ? 1.0 : -1.0, y: 0.0, z: 0.0 }, { x: 0, y: 0, z: 1 });
        }
        const aStemScale = Mat4Tools.getScale(aStemTransform);
        aCtx.current.customTransform.adjustViewStem =
            Mat4Tools.multiply(aStemTranslation, Mat4Tools.multiply(aStemRotation, aStemScale));
        this.adjustPlanMoveStem();
    }
    /**
     * Neck cut height
     */
    neckCutHeight() {
        const aCtx = this.context;
        if (aCtx.engineering.computedTomographyMode == CTMode.CTFree) {
            return 0;
        }
        const anOperationSide = aCtx.engineering.operationSide;
        const aLtLandmark = anOperationSide == OperationSide.Right
            ? aCtx.current.landmarks.femurRightLtProxLandmark
            : aCtx.current.landmarks.femurLeftLtProxLandmark;
        const aResPlanes = StemHeadUtilities.computeStemResectionPlanes(this.context.engineering.stemInfo, this.context.current.transform.stemTGlobal);
        return PatientUtils._myMedicalUtils.calculateNeckCutHeight(aLtLandmark, aResPlanes[0]);
    }
    /**
     * Set Viewer
     * @internal
     */
    static setViewer(theViewer) {
        this._myViewer = theViewer;
        this._myMedicalUtils = new MedicalUtils(this._myViewer);
    }
    get medicalUtils() { return PatientUtils._myMedicalUtils; }
}
PatientUtils._myViewer = null;
/**
 * Set viewer on Init
 * @internal
 */
OccViewer.onInit((theModule) => {
    PatientUtils.setViewer(theModule);
});
